/**
 * Default landing resolver mounted at the index route. Resolves the user's
 * first available menu module (sidebar order: ungrouped first, then groups)
 * from modules.list and redirects there, falling back to the Dashboard when
 * no modules are available or the query fails.
 */
import { Navigate } from "react-router-dom";

import { trpc } from "@/lib/trpc";

/**
 * Resolves the default landing destination for the current user.
 * Shows a loading state while modules.list resolves (shares the query
 * cache with AppShell, so this is usually instant), then redirects to
 * the first available module or /dashboard when none exist.
 */
export function DefaultLanding() {
	const modulesQuery = trpc.modules.list.useQuery();

	if (modulesQuery.isLoading) {
		return (
			<div className="flex min-h-[400px] items-center justify-center">
				<div className="flex flex-col items-center space-y-4">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					<p className="text-sm text-muted-foreground">Loading module...</p>
				</div>
			</div>
		);
	}

	const data = modulesQuery.data;
	const firstSlug = data?.ungrouped[0]?.slug ?? data?.groups[0]?.modules[0]?.slug;

	// No modules or query error — Dashboard remains the safe fallback landing
	if (modulesQuery.isError || !firstSlug) {
		return <Navigate to="/dashboard" replace />;
	}

	return <Navigate to={`/m/${firstSlug}`} replace />;
}
